import { useCallback, useMemo, useState } from 'react'
import { Typography, Checkbox, Tooltip, Stack } from '@mui/material'
import { DataGridPremium, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium'
import { useSnackbar } from '@ui/snackbar'
import AssemblyPlot from '../AssemblyPlot'
import PasteAssemblyFromClipboardButton from '../PasteAssemblyFromClipboardButton'
import { calculateCumulativeWeight, generatePlottingLayers } from '../utils'
import { serializeAssemblyForClipboard } from '../utils/clipboardAssembly'
import { ProjectAssemblyActions } from './actions'
import MultilineEditInput from './components/MultilineEditInput'
import { assemblyKindFormatter, weightFormatter } from './utils'

interface RowType extends Assembly {
  idx: number
  usageCount: number
  usages: string[]
  isStandard: boolean
  weight: number
}

interface Props {
  assemblies: Assembly[]
  /** All assemblies of the catalog; short names are unique across usage types. */
  catalogAssemblies: Assembly[]
  assignments: AssemblyAssignment[]
  usage_type: AssemblyTypes
  onDelete: (assembly: Assembly) => Promise<Assembly[]>
  onEdit: (assembly: Assembly) => void
  onDuplicate: (assembly: Assembly) => void
  onSetStandard: (assembly: Assembly) => void
  isSettingStandard: boolean
  onDownload: (assembly: Assembly) => void
  onStore?: (assembly: Assembly) => Promise<CLTAssembly | TimberFrameAssembly>
  onMutateCell?: (assembly: Assembly) => Promise<void>
  earthMassForce: number
  editable?: boolean
}

// New component for the DataGrid
const AssemblyDataGrid = ({
  assemblies,
  catalogAssemblies,
  assignments,
  usage_type,
  onDelete,
  onEdit,
  onDuplicate,
  onSetStandard,
  isSettingStandard,
  onDownload,
  onStore,
  onMutateCell,
  earthMassForce,
  editable = true,
}: Props) => {
  const { enqueueSnackbar } = useSnackbar()
  const [isDeletingGuid, setIsDeletingGuid] = useState<string | null>(null)

  const rows = useMemo(() => {
    return assemblies.map((assembly, index) => ({
      ...assembly,
      idx: index,
      isStandard: assembly.isStandard ?? false,
      usageCount: assignments.filter(assignment => assignment.assembly_guid === assembly.guid)
        .length,
      usages: assignments
        .filter(assignment => assignment.assembly_guid === assembly.guid)
        .map(assignment => assignment.element_guid),
      weight: calculateCumulativeWeight(
        generatePlottingLayers(
          assembly.layers,
          assembly.usage_type === 'Outer' || assembly.usage_type === 'Inner',
          earthMassForce,
        ),
      ),
    }))
  }, [assemblies, assignments, earthMassForce])

  const columnWidths = {
    standard: 35,
    kind: 100,
    sketch: 80,
    usages: 60,
    weight: 80,
    actions: 250,
    name: 450,
  }

  const defaultColumnProps = {
    align: 'center',
    headerAlign: 'center',
  }

  const handleCopy = useCallback(
    (assembly: Assembly) => {
      try {
        navigator.clipboard.writeText(serializeAssemblyForClipboard(assembly))
        enqueueSnackbar('Aufbau in Zwischenablage kopiert', { variant: 'success' })
      } catch (error) {
        enqueueSnackbar('Fehler beim Kopieren des Aufbaus', { variant: 'error' })
      }
    },
    [enqueueSnackbar],
  )

  const CustomToolbar = () => (
    <Stack direction="row" spacing={1} alignItems="center" p={1}>
      <PasteAssemblyFromClipboardButton usageType={usage_type} onPaste={onDuplicate} />
    </Stack>
  )

  const columns = [
    {
      ...defaultColumnProps,
      field: 'isStandard',
      headerName: 'Std.',
      width: columnWidths.standard,
      renderCell: (params: GridRenderCellParams) => {
        const isStandard = params.row.isStandard

        return (
          <Checkbox
            checked={isStandard}
            disabled={isStandard || isSettingStandard}
            onClick={() => {
              onSetStandard(params.row as Assembly)
            }}
            data-cy={`${usage_type}-list-item-${params.row.idx}-check`}
          />
        )
      },
      type: 'boolean',
    },
    {
      ...defaultColumnProps,
      field: 'kind',
      headerName: 'Typ',
      width: columnWidths.kind,
      valueFormatter: assemblyKindFormatter,
    },
    {
      ...defaultColumnProps,
      field: 'sketch',
      headerName: 'Skizze',
      width: columnWidths.sketch,
      renderCell: (params: GridRenderCellParams<Assembly>) => (
        <Stack display="flex" alignItems="center" justifyContent="center" mt={1}>
          <AssemblyPlot
            assemblyType={params.row.usage_type}
            layers={params.row.layers}
            hasBorder={false}
            outsideWidth={100}
            earthMassForce={earthMassForce}
          />
        </Stack>
      ),
    },
    {
      ...defaultColumnProps,
      field: 'name',
      headerName: 'Name',
      align: 'left',
      headerAlign: 'left',
      flex: 1,
      editable: true,
      renderEditCell: MultilineEditInput,
    },
    {
      ...defaultColumnProps,
      field: 'short_name',
      headerName: 'Kurzbez.',
      editable: true,
      renderEditCell: MultilineEditInput,
    },
    {
      ...defaultColumnProps,
      field: 'usages',
      headerName: 'Anz.',
      description: 'Anzahl der Bauteile mit diesem Aufbau',
      width: columnWidths.usages,
      align: 'center',
      renderCell: (params: GridRenderCellParams<Assembly>) => {
        const assembly = params.row
        const assignedElements = assignments
          .filter(assignment => assignment.assembly_guid === assembly.guid)
          .map(assignment => assignment.element_guid)

        return (
          <Stack alignItems="center" justifyContent="center" height="100%">
            <Tooltip
              title={<Typography fontSize={'small'}>{assignedElements.join('\n')}</Typography>}
              disableHoverListener={assignedElements.length === 0}
            >
              <Typography align="center">{assignedElements.length}</Typography>
            </Tooltip>
          </Stack>
        )
      },
    },
    {
      ...defaultColumnProps,
      field: 'weight',
      headerName: 'Gewicht',
      type: 'number',
      width: columnWidths.weight,
      align: 'center',
      renderHeader: () => (
        <Stack alignItems="center" fontWeight="inherit">
          <Typography fontSize="inherit" fontWeight={500}>
            Gewicht
          </Typography>
          <Typography fontSize="inherit" fontWeight={500}>
            [kN/m²]
          </Typography>
        </Stack>
      ),
      headerAlign: 'center',
      valueFormatter: (value: number) => `${weightFormatter(value)}`,
    },
    {
      ...defaultColumnProps,
      field: 'actions',
      headerName: 'Aktionen',
      align: 'right',
      sortable: false,
      width: columnWidths.actions,
      renderCell: (params: GridRenderCellParams<RowType>) => {
        const rowAssembly = params.row as Assembly
        return (
          <ProjectAssemblyActions
            onEdit={() => onEdit(rowAssembly)}
            onDelete={async () => {
              setIsDeletingGuid(rowAssembly.guid)
              await onDelete(rowAssembly)
              setIsDeletingGuid(null)
            }}
            deleteDisabled={!!isDeletingGuid}
            deleteLoading={isDeletingGuid === rowAssembly.guid}
            onDownload={() => {
              onDownload(rowAssembly)
            }}
            onStore={() => {
              onStore?.(rowAssembly)
            }}
            onDuplicate={() => {
              onDuplicate(rowAssembly)
            }}
            onCopy={() => handleCopy(rowAssembly)}
            dataCy={`${rowAssembly.usage_type}-list-item-${params.row.idx}`}
          />
        )
      },
    },
  ]

  // Function to check if a short_name is unique across all assemblies
  const isShortNameUnique = useCallback(
    (shortName: string, currentAssemblyGuid: string) => {
      return !catalogAssemblies.some(
        assembly => assembly.short_name === shortName && assembly.guid !== currentAssemblyGuid,
      )
    },
    [catalogAssemblies],
  )

  // Function to handle row updates (when inline editing is completed)
  const processRowUpdate = useCallback(
    (newRow: RowType, oldRow: RowType) => {
      // Check if name or short_name has changed
      const nameChanged = newRow.name !== oldRow.name
      const shortNameChanged = newRow.short_name !== oldRow.short_name

      if (shortNameChanged) {
        if (!newRow.short_name.trim()) {
          enqueueSnackbar('Die Kurzbezeichnung darf nicht leer sein.', {
            variant: 'error',
            autoHideDuration: 3000,
          })
          return oldRow
        }

        // Validate that the short_name is unique
        if (!isShortNameUnique(newRow.short_name, newRow.guid)) {
          // Show error notification
          enqueueSnackbar('Die Kurzbezeichnung muss eindeutig sein.', {
            variant: 'error',
            autoHideDuration: 3000,
          })

          // Return the old row to revert changes
          return oldRow
        }
      }

      // If either field has changed and we have a mutation
      if ((nameChanged || shortNameChanged) && onMutateCell) {
        // Use the editMutation to update the assembly
        onMutateCell(newRow as Assembly)
      }

      return newRow
    },
    [enqueueSnackbar, isShortNameUnique, onMutateCell],
  )

  return (
    <DataGridPremium
      rows={rows as RowType[]}
      columns={columns as GridColDef<RowType>[]}
      getRowId={row => row.guid}
      getRowHeight={() => 80}
      columnHeaderHeight={80}
      density="compact"
      disableRowSelectionOnClick
      pageSizeOptions={[100]}
      processRowUpdate={processRowUpdate}
      initialState={{
        pagination: {
          paginationModel: { pageSize: 100 },
        },
      }}
      slots={{
        toolbar: CustomToolbar,
      }}
      sx={{
        backgroundColor: 'white',
        '& .MuiDataGrid-cell': {
          px: 0.75,
        },
        '& .MuiDataGrid-columnHeader': {
          px: 0.75,
        },
      }}
      isCellEditable={() => {
        return editable
      }}
      onCellDoubleClick={() => {
        if (!editable) {
          enqueueSnackbar('Diese Zeile ist gerade nicht editierbar', {
            variant: 'info',
          })
        }
      }}
    />
  )
}

export { AssemblyDataGrid }
